import { useGetSaleEmail, getGetSaleEmailQueryKey } from "@workspace/api-client-react";
import ReactMarkdown from "react-markdown";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

// Per-stage accent classes mirroring the Emails page (listing=blue, sale=green,
// delivery=purple, payment=amber). Used to tint the avatar bubble and the
// email-type badge so the modal reads the same as the inbox.
type Stage = "listing" | "sale" | "delivery" | "payment" | "other";

const STAGE_MEMBERS: Record<Exclude<Stage, "other">, Set<string>> = {
  listing: new Set(["listing_confirmation"]),
  sale: new Set(["sale_notification", "sale_confirmation"]),
  delivery: new Set(["delivery"]),
  payment: new Set(["payment"]),
};

function stageOf(emailType?: string | null): Stage {
  if (!emailType) return "other";
  for (const k of ["listing", "sale", "delivery", "payment"] as const) {
    if (STAGE_MEMBERS[k].has(emailType)) return k;
  }
  return "other";
}

const STAGE_AVATAR: Record<Stage, string> = {
  listing: "bg-blue-500/15 text-blue-400",
  sale: "bg-green-500/15 text-green-400",
  delivery: "bg-purple-500/15 text-purple-400",
  payment: "bg-amber-500/15 text-amber-400",
  other: "bg-muted text-muted-foreground",
};

const STAGE_BADGE: Record<Stage, string> = {
  listing: "border-blue-500/40 bg-blue-500/10 text-blue-300",
  sale: "border-green-500/40 bg-green-500/10 text-green-300",
  delivery: "border-purple-500/40 bg-purple-500/10 text-purple-300",
  payment: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  other: "border-border bg-muted text-muted-foreground",
};

const EMAIL_TYPE_LABEL: Record<string, string> = {
  listing_confirmation: "Listing",
  sale_notification: "Sale (notification)",
  sale_confirmation: "Sale (confirmation)",
  delivery: "Delivery",
  payment: "Payment",
  unknown: "Unknown",
};

function initialOf(...candidates: (string | null | undefined)[]): string {
  for (const c of candidates) {
    const t = c?.trim();
    if (t) return t[0].toUpperCase();
  }
  return "?";
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Read-only email viewer surfaced in-place from the Workspace inventory view.
// Fetches the email via GET /emails/:id and renders rawBody as markdown with
// proper email-viewer chrome. Mutations stay on the standalone Emails page.
export function EmailViewerModal({
  emailId,
  onClose,
}: {
  emailId: number | null;
  onClose: () => void;
}) {
  const isMobile = useIsMobile();
  const { data: detail, isLoading, isError } = useGetSaleEmail(emailId ?? 0, {
    query: {
      enabled: emailId != null,
      queryKey: getGetSaleEmailQueryKey(emailId ?? 0),
    },
  });

  const email = detail?.email;
  const stage = stageOf(email?.emailType);
  const titleText = email?.eventName ?? "Email";
  const subjectText = email?.emailSubject ?? "No subject";

  // Header chrome: tinted avatar, event name, subject and an email-type badge.
  const header = (
    <div className="flex items-start gap-3 text-left">
      <span
        className={cn(
          "flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-base font-semibold",
          STAGE_AVATAR[stage],
        )}
      >
        {initialOf(email?.recipientEmail, email?.eventName)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-base font-semibold text-foreground">
          {titleText}
        </div>
        <div className="truncate text-sm text-muted-foreground">
          {subjectText}
        </div>
        {email?.emailType && (
          <div className="mt-2">
            <Badge
              variant="outline"
              className={cn("text-[11px]", STAGE_BADGE[stage])}
            >
              {EMAIL_TYPE_LABEL[email.emailType] ?? email.emailType}
            </Badge>
          </div>
        )}
      </div>
    </div>
  );

  const metaGrid = (
    <div className="grid grid-cols-[64px_1fr] gap-x-3 gap-y-1.5 border-t border-border pt-3 text-sm">
      <span className="text-muted-foreground">From</span>
      <span className="text-foreground">Ticketmaster</span>
      <span className="text-muted-foreground">To</span>
      <span className="truncate text-foreground">
        {email?.recipientEmail ?? "unknown recipient"}
      </span>
      <span className="text-muted-foreground">Date</span>
      <span className="text-foreground">{fmtDate(email?.receivedAt)}</span>
      <span className="text-muted-foreground">Listing</span>
      <span className="font-mono text-foreground">
        #{email?.listingNumber ?? "—"}
      </span>
    </div>
  );

  const body = (() => {
    if (isLoading) {
      return (
        <div className="space-y-3">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      );
    }
    if (isError) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-6 text-center text-sm text-destructive">
          <AlertTriangle className="h-5 w-5" />
          <p>Couldn&apos;t load this email. Please try again.</p>
        </div>
      );
    }
    if (!detail) {
      return (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      );
    }
    if (!detail.rawBody) {
      return (
        <div className="rounded-md border border-border bg-card p-4 text-sm text-muted-foreground">
          No email body available.
        </div>
      );
    }
    return (
      <div className="rounded-md border border-border bg-card p-4">
        <div className="prose prose-sm prose-invert max-w-none leading-relaxed prose-headings:text-foreground prose-p:my-2 prose-a:text-primary prose-strong:text-foreground prose-li:my-0.5 prose-table:text-xs">
          <ReactMarkdown
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer" />
              ),
            }}
          >
            {detail.rawBody}
          </ReactMarkdown>
        </div>
      </div>
    );
  })();

  // On phones render a bottom sheet (consistent with the app's other mobile
  // overlays); on md+ a centered dialog. The body scrolls independently of the
  // pinned header in both layouts.
  if (isMobile) {
    return (
      <Sheet open={emailId != null} onOpenChange={(o) => !o && onClose()}>
        <SheetContent
          side="bottom"
          className="flex max-h-[88vh] flex-col gap-0 rounded-t-xl p-0"
        >
          <SheetHeader className="space-y-3 border-b border-border p-4 pr-10 text-left">
            <SheetTitle className="sr-only">{titleText}</SheetTitle>
            <SheetDescription className="sr-only">{subjectText}</SheetDescription>
            {header}
            {!isLoading && !isError && metaGrid}
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">{body}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={emailId != null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[85vh] w-full max-w-2xl flex-col gap-0 p-0">
        <DialogHeader className="space-y-3 border-b border-border p-5 pr-10">
          <DialogTitle className="sr-only">{titleText}</DialogTitle>
          <DialogDescription className="sr-only">{subjectText}</DialogDescription>
          {header}
          {!isLoading && !isError && metaGrid}
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto p-5">{body}</div>
      </DialogContent>
    </Dialog>
  );
}
